import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import UserDetails from "./UserDetails";
import {
    getAllUsers,
    getPendingInvitations,
    inviteUser,
    cancelInvitation,
    resendInvitation,
    deleteUser,
    formatPhoneDisplay,
    formatPhoneInput,
    validateAustralianPhone
} from "../../lib/supabase/users";

import { MdVerified, MdEmail, MdRefresh, MdClose } from 'react-icons/md';
import {
    FiEdit3, FiTrash2, FiUsers, FiUserPlus,
    FiShield, FiTool, FiTruck, FiDollarSign,
} from 'react-icons/fi';
import ConfirmationPopup from "../popup/ConfirmationPopup";
import { hasPermission } from "../../lib/permissions";

// ─── Role config ──────────────────────────────────────────────────────────────

const CATEGORIES = [
    {
        key: "admins",
        label: "Admins",
        singular: "Admin",
        color: "bg-blue-500",
        lightBg: "bg-blue-50",
        textColor: "text-blue-700",
        border: "border-blue-500",
        avatarBg: "bg-blue-100 text-blue-700",
        icon: FiShield,
        tabActive: "bg-blue-500 text-white shadow-md",
        tabInactive: "text-blue-600 hover:bg-blue-50",
    },
    {
        key: "designers",
        label: "Designers",
        singular: "Designer",
        color: "bg-emerald-500",
        lightBg: "bg-emerald-50",
        textColor: "text-emerald-700",
        border: "border-emerald-500",
        avatarBg: "bg-emerald-100 text-emerald-700",
        icon: FiTool,
        tabActive: "bg-emerald-500 text-white shadow-md",
        tabInactive: "text-emerald-600 hover:bg-emerald-50",
    },
    {
        key: "estimators",
        label: "Estimators",
        singular: "Estimator",
        color: "bg-amber-500",
        lightBg: "bg-amber-50",
        textColor: "text-amber-700",
        border: "border-amber-500",
        avatarBg: "bg-amber-100 text-amber-700",
        icon: FiDollarSign,
        tabActive: "bg-amber-500 text-white shadow-md",
        tabInactive: "text-amber-600 hover:bg-amber-50",
    },
    {
        key: "drivers",
        label: "Drivers",
        singular: "Driver",
        color: "bg-rose-500",
        lightBg: "bg-rose-50",
        textColor: "text-rose-700",
        border: "border-rose-500",
        avatarBg: "bg-rose-100 text-rose-700",
        icon: FiTruck,
        tabActive: "bg-rose-500 text-white shadow-md",
        tabInactive: "text-rose-600 hover:bg-rose-50",
    },
];

// ─── UserAvatar ───────────────────────────────────────────────────────────────

const UserAvatar = ({ user, avatarBg }) => {
    const initial = (user.full_name || user.username || '?')[0].toUpperCase();

    if (user.avatar_url) {
        return (
            <img
                src={user.avatar_url}
                alt={user.full_name || user.username}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0"
            />
        );
    }

    return (
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ring-2 ring-white shadow-sm flex-shrink-0 ${avatarBg}`}>
            {initial}
        </div>
    );
};

// ─── Users component ──────────────────────────────────────────────────────────

const Users = () => {
    const { userProfile, company } = useAuth();
    const [users, setUsers] = useState({ admins: [], designers: [], estimators: [], drivers: [] });
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [activeCategory, setActiveCategory] = useState("admins");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showAddUserPopup, setShowAddUserPopup] = useState(false);
    const [showInvitationsPopup, setShowInvitationsPopup] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        username: "", email: "", role: "", password: "", phone: "", userType: "admin"
    });

    useEffect(() => {
        if (company?.id) fetchAllData();
    }, [company]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const [usersData, invitationsData] = await Promise.all([
                getAllUsers(company.id),
                getPendingInvitations(company.id),
            ]);
            setUsers(usersData);
            setPendingInvitations(invitationsData);
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Failed to load users: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            alert("Please enter a valid email address");
            return;
        }
        if (formData.phone && !validateAustralianPhone(formData.phone)) {
            alert("Please enter a valid Australian phone number");
            return;
        }
        try {
            setLoading(true);
            const result = await inviteUser(
                formData, company.id, company.name,
                userProfile.id, userProfile.username || userProfile.full_name
            );
            alert(result.message + "\n\nInvitation URL: " + result.inviteUrl);
            setFormData({ username: "", email: "", role: "", password: "", phone: "", userType: "admin" });
            setShowAddUserPopup(false);
            await fetchAllData();
        } catch (error) {
            console.error("Error inviting user:", error);
            alert("Failed to send invitation: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = (user) => {
        setPendingAction({
            type: 'delete',
            data: user,
            message: `Are you sure you want to delete ${user.username}${user.position ? ` (${user.position})` : ''}?`,
        });
        setShowConfirmation(true);
    };

    const handleCancelInvitation = (invitation) => {
        setPendingAction({
            type: 'cancel_invitation',
            data: invitation,
            message: `Are you sure you want to cancel the invitation for ${invitation.email}?`,
        });
        setShowConfirmation(true);
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            setLoading(true);
            const result = await resendInvitation(invitationId);
            alert(result.message);
            await fetchAllData();
        } catch (error) {
            console.error("Error resending invitation:", error);
            alert("Failed to resend invitation: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAction = async () => {
        try {
            setLoading(true);
            if (pendingAction.type === 'delete') {
                if (pendingAction.data.id === userProfile.id) { alert("Cannot delete your own account"); return; }
                if (pendingAction.data.role === 'super_admin') { alert("Cannot delete super admin users"); return; }
                await deleteUser(pendingAction.data.id);
                alert("User deleted successfully");
            } else if (pendingAction.type === 'cancel_invitation') {
                await cancelInvitation(pendingAction.data.id);
                alert("Invitation cancelled successfully");
            }
            await fetchAllData();
        } catch (error) {
            console.error("Error performing action:", error);
            alert("Action failed: " + error.message);
        } finally {
            setLoading(false);
            setShowConfirmation(false);
            setPendingAction(null);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === "phone" ? formatPhoneInput(value) : value,
        }));
    };

    // ── Card renderer ─────────────────────────────────────────────────────────

    const renderUserCard = (user, userType) => {
        const isOnline = false;
        const isCurrentUser = user.id === userProfile?.id;
        const isSuperAdmin = userProfile?.role === 'super_admin';
        const canDelete = isSuperAdmin && user.role !== 'super_admin' && !isCurrentUser;
        const cat = CATEGORIES.find(c => c.key === userType);
        const roleSingular = user.role === 'super_admin' ? 'Super Admin' : cat.singular;

        return (
            <div
                key={user.id}
                className={`users-mobile-card relative bg-white border rounded-xl p-3 shadow-sm hover:shadow-md transition-all flex flex-col gap-2.5 ${isCurrentUser
                    ? 'ring-2 ring-blue-400 border-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
            >
                {/* Online dot */}
                <div
                    className={`absolute top-3.5 right-3.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${isOnline ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                    title={isOnline ? 'Online' : 'Offline'}
                />

                {/* Avatar + name */}
                <div className="flex items-center gap-2.5 pr-4">
                    <UserAvatar user={user} avatarBg={cat.avatarBg} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-gray-900 text-sm leading-tight">
                                {user.full_name || user.username}
                            </span>
                            {isCurrentUser && (
                                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
                                    You
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-gray-400 font-medium">@{user.username}</span>
                    </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-1.5">
                    <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full text-white ${cat.color}`}>
                        {roleSingular}
                    </span>
                    {userType === 'designers' && user.department && (
                        <span className="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 capitalize">
                            {user.department}
                        </span>
                    )}
                    {user.role === 'super_admin' && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                            <MdVerified className="w-3 h-3" />
                            Verified
                        </span>
                    )}
                </div>

                {/* Position */}
                {user.position && (
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-md px-2 py-1 w-fit">
                        <span className="text-[11px] font-semibold text-gray-700 leading-tight">{user.position}</span>
                    </div>
                )}

                {/* Footer */}
                <div className="users-card-footer flex items-center justify-between pt-2 mt-auto border-t border-gray-100">
                    <a
                        href={`mailto:${user.email}`}
                        className="text-[11px] text-gray-400 hover:text-blue-600 transition-colors break-all"
                        onClick={e => e.stopPropagation()}
                    >
                        {user.email}
                    </a>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                            <button
                                onClick={() => setSelectedUserId(user.id)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Edit user"
                            >
                                <FiEdit3 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Delete user"
                            >
                                <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ── Invitation card ───────────────────────────────────────────────────────

    const renderInvitationCard = (invitation) => {
        const isExpired = new Date(invitation.expires_at) < new Date();
        return (
            <div
                key={invitation.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-orange-200 flex flex-col sm:flex-row sm:items-center gap-3"
            >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                        <MdEmail className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                            <span className="font-semibold text-gray-900 text-sm truncate">{invitation.email}</span>
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-500 text-white capitalize">
                                {invitation.role}
                            </span>
                            {isExpired && (
                                <span className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Expired</span>
                            )}
                        </div>
                        <span className="text-xs text-gray-400">@{invitation.username}</span>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Invited by {invitation.invited_by_name} · Expires {new Date(invitation.expires_at).toLocaleDateString()}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 self-end sm:self-auto flex-shrink-0">
                    <button
                        onClick={() => handleResendInvitation(invitation.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors font-medium"
                        title="Resend invitation"
                    >
                        <MdRefresh className="w-3.5 h-3.5" />
                        Resend
                    </button>
                    <button
                        onClick={() => handleCancelInvitation(invitation)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg transition-colors font-medium"
                        title="Cancel invitation"
                    >
                        <MdClose className="w-3.5 h-3.5" />
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    // ── Loading ───────────────────────────────────────────────────────────────

    if (loading && !users.admins.length) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
        );
    }

    const activeCat = CATEGORIES.find(c => c.key === activeCategory);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="users-page mt-0">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Users</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {hasPermission(userProfile?.role, ["super_admin", "admin"])
                            ? "Manage team members and pending invitations"
                            : "View team members and their contacts"}
                    </p>
                </div>
                {userProfile?.role?.includes('admin') && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {pendingInvitations.length > 0 && (
                            <button
                                onClick={() => setShowInvitationsPopup(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded-lg text-sm font-semibold transition-colors"
                            >
                                <MdEmail className="w-4 h-4" />
                                <span className="hidden sm:inline">Invitations</span>
                                <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                                    {pendingInvitations.length}
                                </span>
                            </button>
                        )}
                        <button
                            onClick={() => setShowAddUserPopup(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
                        >
                            <FiUserPlus className="w-4 h-4" />
                            <span className="hidden sm:inline">Invite User</span>
                            <span className="sm:hidden">Invite</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Main panel */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Category tabs */}
                <div className="users-role-tabs flex flex-wrap gap-1 p-3 bg-gray-50 border-b border-gray-200">
                    {CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        const count = users[cat.key]?.length || 0;
                        const isActive = activeCategory === cat.key;
                        return (
                            <button
                                key={cat.key}
                                onClick={() => setActiveCategory(cat.key)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? cat.tabActive : `bg-white text-gray-600 border border-gray-200 ${cat.tabInactive}`
                                    }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {cat.label}
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${isActive ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Cards grid */}
                <div className="p-4">
                    {users[activeCategory]?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {[...users[activeCategory]]
                                .sort((a, b) => {
                                    const order = {
                                        roof: 1,
                                        wall: 2,
                                        floor: 3,
                                        null: 4
                                    };

                                    const aDept = a?.department ?? "null";
                                    const bDept = b?.department ?? "null";

                                    return (order[aDept] || 5) - (order[bDept] || 5);
                                })
                                .map((user) => renderUserCard(user, activeCategory))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className={`w-14 h-14 rounded-full ${activeCat.lightBg} flex items-center justify-center mb-3`}>
                                <FiUsers className={`w-6 h-6 ${activeCat.textColor}`} />
                            </div>
                            <p className="text-gray-500 font-medium">No {activeCat.label.toLowerCase()} found</p>
                            <p className="text-gray-400 text-sm mt-1">Invite users to get started</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Add User Popup ───────────────────────────────────────────── */}
            {showAddUserPopup && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <FiUserPlus className="w-5 h-5 text-teal-600" />
                                Invite New User
                            </h2>
                            <button
                                onClick={() => setShowAddUserPopup(false)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAddUser} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">User Type</label>
                                <select
                                    name="userType"
                                    value={formData.userType}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                    required
                                >
                                    <option value="admin">Admin</option>
                                    <option value="designer">Designer</option>
                                    <option value="estimator">Estimator</option>
                                    <option value="driver">Driver</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                    placeholder="user@example.com"
                                    required
                                />
                                <p className="text-xs text-gray-400 mt-1">An invitation will be sent to this email</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                    placeholder="Enter username"
                                    required
                                />
                            </div>

                            {formData.userType === "admin" && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position (Optional)</label>
                                    <input
                                        type="text"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="e.g., Manager, Supervisor"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number (Optional)</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-3 py-2 text-sm bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-gray-600">
                                        +61
                                    </span>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="433 177 867"
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Mobile (04xx) or landline (02/03/07/08)</p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-xs text-blue-800">
                                    <strong>Note:</strong> An invitation email will be sent. The user sets their own password when accepting.
                                </p>
                            </div>

                            <div className="flex gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setShowAddUserPopup(false)}
                                    className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Sending…' : 'Send Invitation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Pending Invitations Popup ────────────────────────────────── */}
            {showInvitationsPopup && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <MdEmail className="w-5 h-5 text-orange-500" />
                                Pending Invitations
                                <span className="ml-1 bg-orange-100 text-orange-700 text-sm font-bold px-2 py-0.5 rounded-full">
                                    {pendingInvitations.length}
                                </span>
                            </h2>
                            <button
                                onClick={() => setShowInvitationsPopup(false)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {pendingInvitations.length > 0 ? (
                                <div className="space-y-3">
                                    {pendingInvitations.map((inv) => renderInvitationCard(inv))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <MdEmail className="w-12 h-12 text-gray-300 mb-3" />
                                    <p className="text-gray-500">No pending invitations</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation */}
            <ConfirmationPopup
                isOpen={showConfirmation}
                title={pendingAction?.type === 'delete' ? 'Delete User' : 'Cancel Invitation'}
                message={pendingAction?.message || ''}
                confirmText={pendingAction?.type === 'delete' ? 'Delete' : 'Cancel Invitation'}
                cancelText="Go Back"
                onConfirm={handleConfirmAction}
                onCancel={() => { setShowConfirmation(false); setPendingAction(null); }}
            />

            {/* User details modal */}
            {selectedUserId && (
                <UserDetails
                    modalUserId={selectedUserId}
                    onClose={(deleted) => {
                        setSelectedUserId(null);
                        if (deleted) fetchAllData();
                    }}
                />
            )}
        </div>
    );
};

export default Users;
